import { openDB, DBSchema, IDBPDatabase } from 'idb'

// Define the database schema
interface OfflineDB extends DBSchema {
    pendingTransactions: {
        key: string
        value: PendingTransaction
        indexes: {
            'by-type': string
            'by-timestamp': number
            'by-status': string
        }
    }
    syncLog: {
        key: string
        value: SyncLogEntry
        indexes: {
            'by-timestamp': number
        }
    }
}

export interface PendingTransaction {
    id: string
    type: 'card_refuel' | 'attendant_refuel' | 'subcontractor_refuel' | 'tank_refilling'
    data: any
    timestamp: number
    status: 'pending' | 'syncing' | 'failed' | 'synced'
    retryCount: number
    error?: string
    photos?: {
        slipPhoto?: Blob
        voucherPhoto?: Blob
        odoBeforePhoto?: Blob
        odoAfterPhoto?: Blob
    }
}

export interface SyncLogEntry {
    id: string
    transactionId: string
    timestamp: number
    success: boolean
    error?: string
}

class OfflineStorageService {
    private db: IDBPDatabase<OfflineDB> | null = null
    private readonly DB_NAME = 'FMDProOfflineDB'
    private readonly DB_VERSION = 1

    async init(): Promise<void> {
        if (this.db) return

        this.db = await openDB<OfflineDB>(this.DB_NAME, this.DB_VERSION, {
            upgrade(db) {
                // Create pendingTransactions store
                if (!db.objectStoreNames.contains('pendingTransactions')) {
                    const txStore = db.createObjectStore('pendingTransactions', {
                        keyPath: 'id'
                    })
                    txStore.createIndex('by-type', 'type')
                    txStore.createIndex('by-timestamp', 'timestamp')
                    txStore.createIndex('by-status', 'status')
                }

                // Create syncLog store
                if (!db.objectStoreNames.contains('syncLog')) {
                    const logStore = db.createObjectStore('syncLog', {
                        keyPath: 'id'
                    })
                    logStore.createIndex('by-timestamp', 'timestamp')
                }
            }
        })
    }

    async addPendingTransaction(
        type: PendingTransaction['type'],
        data: any,
        photos?: PendingTransaction['photos']
    ): Promise<string> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        // Check if user is authenticated
        const isAuthenticated = this.checkAuthentication()
        if (!isAuthenticated) {
            throw new Error('You must be logged in to work offline. Please log in first.')
        }

        // Get user info from localStorage
        const userId = localStorage.getItem('userId')
        const userEmail = localStorage.getItem('userEmail')

        const transaction: PendingTransaction = {
            id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type,
            data: {
                ...data,
                // Ensure user attribution is preserved
                created_by_user_id: userId,
                created_by_user_email: userEmail,
                offline_created_at: new Date().toISOString()
            },
            timestamp: Date.now(),
            status: 'pending',
            retryCount: 0,
            photos
        }

        await this.db.add('pendingTransactions', transaction)
        console.log('[OfflineStorage] Added pending transaction:', transaction.id, 'by user:', userEmail)
        return transaction.id
    }

    async getPendingTransactions(): Promise<PendingTransaction[]> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const transactions = await this.db.getAllFromIndex(
            'pendingTransactions',
            'by-status',
            'pending'
        )
        return transactions.sort((a, b) => a.timestamp - b.timestamp)
    }

    async getTransactionById(id: string): Promise<PendingTransaction | undefined> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        return await this.db.get('pendingTransactions', id)
    }

    async updateTransactionStatus(
        id: string,
        status: PendingTransaction['status'],
        error?: string
    ): Promise<void> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const transaction = await this.db.get('pendingTransactions', id)
        if (!transaction) {
            console.warn('[OfflineStorage] Transaction not found:', id)
            return
        }

        transaction.status = status
        if (error) transaction.error = error
        if (status === 'failed') transaction.retryCount++

        await this.db.put('pendingTransactions', transaction)
        console.log('[OfflineStorage] Updated transaction status:', id, status)
    }

    async deleteTransaction(id: string): Promise<void> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        await this.db.delete('pendingTransactions', id)
        console.log('[OfflineStorage] Deleted transaction:', id)
    }

    async getPendingCount(): Promise<number> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const transactions = await this.getPendingTransactions()
        return transactions.length
    }

    async addSyncLog(transactionId: string, success: boolean, error?: string): Promise<void> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const logEntry: SyncLogEntry = {
            id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            transactionId,
            timestamp: Date.now(),
            success,
            error
        }

        await this.db.add('syncLog', logEntry)
    }

    async getSyncLogs(limit: number = 50): Promise<SyncLogEntry[]> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const logs = await this.db.getAll('syncLog')
        return logs
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, limit)
    }

    async clearSyncedTransactions(): Promise<void> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        const allTransactions = await this.db.getAll('pendingTransactions')
        const syncedTransactions = allTransactions.filter(tx => tx.status === 'synced')

        for (const tx of syncedTransactions) {
            await this.db.delete('pendingTransactions', tx.id)
        }

        console.log('[OfflineStorage] Cleared', syncedTransactions.length, 'synced transactions')
    }

    async getAllTransactions(): Promise<PendingTransaction[]> {
        await this.init()
        if (!this.db) throw new Error('Database not initialized')

        return await this.db.getAll('pendingTransactions')
    }

    /**
     * Check if user is authenticated by verifying localStorage auth data
     */
    private checkAuthentication(): boolean {
        const user = localStorage.getItem('supabase_user')
        const session = localStorage.getItem('supabase_session')
        const userId = localStorage.getItem('userId')
        const loggedOut = localStorage.getItem('loggedOut')

        // User must have valid session data and not be marked as logged out
        if (!user || !session || !userId || loggedOut === 'true') {
            console.warn('[OfflineStorage] User not authenticated for offline operations')
            return false
        }

        try {
            const parsedUser = JSON.parse(user)
            const parsedSession = JSON.parse(session)

            // Verify user has an ID
            if (!parsedUser.id || parsedUser.id !== userId) {
                console.warn('[OfflineStorage] User ID mismatch')
                return false
            }

            // Check if session has expired (if it has an expiry)
            if (parsedSession.expires_at) {
                const expiresAt = new Date(parsedSession.expires_at).getTime()
                if (expiresAt < Date.now()) {
                    console.warn('[OfflineStorage] Session has expired')
                    return false
                }
            }

            return true
        } catch (error) {
            console.error('[OfflineStorage] Error parsing auth data:', error)
            return false
        }
    }

    /**
     * Get current authenticated user info
     */
    public getAuthenticatedUser(): { id: string; email: string } | null {
        if (!this.checkAuthentication()) {
            return null
        }

        const userId = localStorage.getItem('userId')
        const userEmail = localStorage.getItem('userEmail')

        if (!userId || !userEmail) {
            return null
        }

        return { id: userId, email: userEmail }
    }
}

export const offlineStorageService = new OfflineStorageService()
